'use client';

import { useState, useCallback } from 'react';
import Sidebar from '@/components/common/Sidebar';
import DashboardInteractive from './components/DashboardInteractive';

const ASSIGNEE_OPTIONS = [
  { value: 'Sarah', label: 'Sarah', name: 'Sarah Chen', avatar: 'https://img.rocket.new/generatedImages/rocket_gen_img_103b528db-1763293982935.png', alt: 'Sarah' },
  { value: 'Michael', label: 'Michael', name: 'Michael Chen', avatar: 'https://img.rocket.new/generatedImages/rocket_gen_img_1deaf0e87-1763296711015.png', alt: 'Michael' },
  { value: 'Jennifer', label: 'Jennifer', name: 'Jennifer Chen', avatar: 'https://img.rocket.new/generatedImages/rocket_gen_img_1a36548bd-1763296665300.png', alt: 'Jennifer' },
  { value: 'Anyone', label: 'Anyone', name: 'Anyone', avatar: 'https://img.rocket.new/generatedImages/rocket_gen_img_1663cf851-1767842875103.png', alt: 'Anyone' },
];

const DUE_OPTIONS = [
  { value: 'Today', label: 'Today' },
  { value: 'Tomorrow', label: 'Tomorrow' },
  { value: 'This week', label: 'This week' },
];

export default function DashboardPage() {
  const mockAlerts = [
  {
    id: 'alert-1',
    type: 'medication' as const,
    title: 'Missed Morning Medication',
    description:
    'Mom missed her 9:00 AM Lisinopril dose. This medication is critical for blood pressure management.',
    timestamp: '2 hours ago',
    priority: 'urgent' as const,
    actionUrl: '/medication-manager',
    actionLabel: 'Log Medication'
  },
  {
    id: 'alert-2',
    type: 'checkin' as const,
    title: 'Weekly Check-In Due',
    description:
    'It\'s been 7 days since the last wellness check-in call. Schedule a call to monitor Mom\'s health status.',
    timestamp: '1 day ago',
    priority: 'high' as const,
    actionUrl: '/check-ins',
    actionLabel: 'Schedule Call'
  }];


  const mockActivities = [
  {
    id: 'activity-1',
    type: 'medication' as const,
    title: 'Sarah logged evening medications',
    description: 'Completed all 3 medications for 8:00 PM dose',
    timestamp: '30 mins ago',
    user: {
      name: 'Sarah Chen',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_103b528db-1763293982935.png",
      alt: 'Professional woman with long brown hair wearing white blouse smiling at camera'
    },
    metadata: {
      status: 'Completed',
      value: '3 medications'
    }
  },
  {
    id: 'activity-2',
    type: 'task' as const,
    title: 'Michael completed grocery shopping',
    description: 'Picked up prescriptions and weekly groceries',
    timestamp: '2 hours ago',
    user: {
      name: 'Michael Chen',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1deaf0e87-1763296711015.png",
      alt: 'Asian man with short black hair in navy blue shirt smiling outdoors'
    },
    metadata: {
      status: 'Completed'
    }
  },
  {
    id: 'activity-3',
    type: 'checkin' as const,
    title: 'AI Check-In Call Completed',
    description: 'Wellness score: 8.5/10 - Mom reported feeling good today',
    timestamp: '5 hours ago',
    user: {
      name: 'Kin AI',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1663cf851-1767842875103.png",
      alt: 'Abstract AI technology visualization with blue and purple gradient'
    },
    metadata: {
      value: 'Score: 8.5/10'
    }
  },
  {
    id: 'activity-4',
    type: 'document' as const,
    title: 'Emily uploaded Medicare card',
    description: 'New insurance document added to vault',
    timestamp: '1 day ago',
    user: {
      name: 'Emily Chen',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1a36548bd-1763296665300.png",
      alt: 'Hispanic woman with dark wavy hair wearing red top smiling warmly'
    },
    metadata: {
      status: 'Uploaded'
    }
  }];


  const mockQuickActions = [
  {
    id: 'action-1',
    label: 'Log Medication',
    description: 'Record a medication dose',
    icon: 'BeakerIcon',
    href: '/medication-manager',
    color: 'primary' as const
  },
  {
    id: 'action-2',
    label: 'Video Check-In',
    description: 'Visual wellness assessment',
    icon: 'VideoCameraIcon',
    href: '/video-check-in',
    color: 'secondary' as const
  },
  {
    id: 'action-3',
    label: 'Upload Document',
    description: 'Add to document vault',
    icon: 'DocumentTextIcon',
    href: '/documents',
    color: 'accent' as const
  },
  {
    id: 'action-4',
    label: 'Create Task',
    description: 'Assign family task',
    icon: 'PlusCircleIcon',
    href: '/family-coordination-hub',
    color: 'success' as const
  }];


  const mockScheduleItems = [
  {
    id: 'schedule-1',
    time: '9:00 AM',
    title: 'Lisinopril 10mg',
    type: 'medication' as const,
    status: 'missed' as const,
    details: 'Blood pressure medication'
  },
  {
    id: 'schedule-2',
    time: '12:00 PM',
    title: 'Lunch & Metformin',
    type: 'medication' as const,
    status: 'pending' as const,
    details: 'Take with food'
  },
  {
    id: 'schedule-3',
    time: '2:00 PM',
    title: 'Physical Therapy',
    type: 'appointment' as const,
    status: 'pending' as const,
    details: 'Dr. Martinez - 45 min session'
  },
  {
    id: 'schedule-4',
    time: '8:00 PM',
    title: 'Evening Medications',
    type: 'medication' as const,
    status: 'pending' as const,
    details: '3 medications'
  }];


  const initialFamilyTasks = [
  {
    id: 'task-1',
    title: 'Pick up prescription refills',
    assignee: {
      name: 'Sarah Chen',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_103b528db-1763293982935.png",
      alt: 'Professional woman with long brown hair wearing white blouse smiling at camera'
    },
    dueDate: 'Today',
    priority: 'high' as const,
    status: 'in-progress' as const
  },
  {
    id: 'task-2',
    title: 'Schedule cardiology follow-up',
    assignee: {
      name: 'Michael Chen',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1deaf0e87-1763296711015.png",
      alt: 'Asian man with short black hair in navy blue shirt smiling outdoors'
    },
    dueDate: 'Tomorrow',
    priority: 'medium' as const,
    status: 'pending' as const
  },
  {
    id: 'task-3',
    title: 'Review Medicare Advantage plans',
    assignee: {
      name: 'Emily Chen',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1a36548bd-1763296665300.png",
      alt: 'Hispanic woman with dark wavy hair wearing red top smiling warmly'
    },
    dueDate: 'Feb 20',
    priority: 'low' as const,
    status: 'pending' as const
  }
  ];

  const [familyTasks, setFamilyTasks] = useState(initialFamilyTasks);
  const [createTaskModalOpen, setCreateTaskModalOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [taskName, setTaskName] = useState('');
  const [taskAssignTo, setTaskAssignTo] = useState('Sarah');
  const [taskDueDate, setTaskDueDate] = useState('Today');
  const [taskIdCounter, setTaskIdCounter] = useState(4);

  const openCreateTaskModal = useCallback(() => setCreateTaskModalOpen(true), []);
  const closeCreateTaskModal = useCallback(() => {
    setCreateTaskModalOpen(false);
    setTaskName('');
    setTaskAssignTo('Sarah');
    setTaskDueDate('Today');
  }, []);

  const handleAddTask = useCallback(() => {
    const trimmed = taskName.trim();
    if (!trimmed) return;
    const assigneeOption = ASSIGNEE_OPTIONS.find((o) => o.value === taskAssignTo)!;
    setFamilyTasks((prev) => [
      ...prev,
      {
        id: `task-${taskIdCounter}`,
        title: trimmed,
        assignee: {
          name: assigneeOption.name,
          avatar: assigneeOption.avatar,
          alt: assigneeOption.alt,
        },
        dueDate: taskDueDate,
        priority: 'medium' as const,
        status: 'pending' as const,
      },
    ]);
    setTaskIdCounter((c) => c + 1);
    closeCreateTaskModal();
    setToast('Task added!');
    setTimeout(() => setToast(null), 3000);
  }, [taskName, taskAssignTo, taskDueDate, taskIdCounter, closeCreateTaskModal]);


  const mockUpcomingItems = [
  {
    id: 'upcoming-1',
    title: 'Cardiology Appointment',
    date: 'Feb 16',
    type: 'appointment' as const,
    description: 'Dr. Johnson - Annual heart checkup'
  },
  {
    id: 'upcoming-2',
    title: 'Medication Refill Due',
    date: 'Feb 18',
    type: 'refill' as const,
    description: 'Lisinopril prescription expires'
  },
  {
    id: 'upcoming-3',
    title: 'Medicare Enrollment Deadline',
    date: 'Feb 28',
    type: 'deadline' as const,
    description: 'Annual enrollment period ends'
  },
  {
    id: 'upcoming-4',
    title: 'Weekly Wellness Check-In',
    date: 'Feb 21',
    type: 'checkin' as const,
    description: 'Scheduled AI call for health monitoring'
  }];


  const mockFamilyMembers = [
  {
    id: 'member-1',
    name: 'Sarah Chen',
    isOnline: true,
    lastActive: 'Active now'
  },
  {
    id: 'member-2',
    name: 'Michael Chen',
    isOnline: true,
    lastActive: 'Active now'
  },
  {
    id: 'member-3',
    name: 'Emily Chen',
    isOnline: false,
    lastActive: '2 hours ago'
  }];


  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <main className="pt-20 pb-[88px] lg:pb-8 max-w-[960px] mx-auto px-6 w-full">
        <DashboardInteractive
          alerts={mockAlerts}
          activities={mockActivities}
          quickActions={mockQuickActions}
          scheduleItems={mockScheduleItems}
          familyTasks={familyTasks}
          upcomingItems={mockUpcomingItems}
          familyMembers={mockFamilyMembers}
          onCreateTaskClick={openCreateTaskModal}
        />
      </main>

      {/* Create Task modal */}
      {createTaskModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={closeCreateTaskModal}>
          <div
            className="glass w-full max-w-md p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-heading font-semibold text-foreground mb-4">Create Task</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Task name</label>
                <input
                  type="text"
                  value={taskName}
                  onChange={(e) => setTaskName(e.target.value)}
                  placeholder="e.g. Pick up Metformin"
                  className="w-full px-4 py-2.5 rounded-lg border border-[#E0DBD3] bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-[#D6AB47]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Assign to</label>
                <select
                  value={taskAssignTo}
                  onChange={(e) => setTaskAssignTo(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg border border-[#E0DBD3] bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-[#D6AB47]"
                >
                  {ASSIGNEE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Due date</label>
                <select
                  value={taskDueDate}
                  onChange={(e) => setTaskDueDate(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg border border-[#E0DBD3] bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-[#D6AB47]"
                >
                  {DUE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={handleAddTask}
                className="flex-1 py-2.5 rounded-lg font-medium text-[#1A1714] bg-[#D6AB47] hover:bg-[#D6AB47]/90 transition-colors"
              >
                Add Task
              </button>
              <button
                type="button"
                onClick={closeCreateTaskModal}
                className="flex-1 py-2.5 rounded-lg font-medium text-foreground border border-[#E0DBD3] hover:bg-[#E8E4DC] transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-lg glass text-sm font-medium text-foreground animate-in fade-in duration-200">
          {toast}
        </div>
      )}
    </div>
  );
}